import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  Optional,
  ViewEncapsulation,
  forwardRef,
  inject,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';

@UntilDestroy()
@Component({
  selector: 'app-calendar-header',
  templateUrl: './calendar-header.component.html',
  styleUrls: ['./calendar-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CalendarHeaderComponent extends ComponentAbstract implements OnInit {
  private readonly timezoneService: TimezoneService = inject(TimezoneService);
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  readonly minDate = new Date(2022, 0, 1);
  months: string[] = [];
  years: number[] = [];
  selectedMonthIndex: number;
  selectedYear: number;
  private maxDate: Date;
  monthMenuOpen = false;
  yearMenuOpen = false;

  constructor(
    @Inject(forwardRef(() => MatCalendar)) public calendar: MatCalendar<Date>,
    @Optional() private _dateAdapter: DateAdapter<Date>
  ) {
    super(inject(ChangeDetectorRef));
  }

  ngOnInit(): void {
    this.months = this._dateAdapter ? this._dateAdapter.getMonthNames('long') : [];
    this.initializeYearOptions();
    this.ensureActiveDateWithinBounds();
    this.syncSelectionWithActiveDate();

    this.calendar.stateChanges
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.ensureActiveDateWithinBounds();
          this.syncSelectionWithActiveDate();
          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  previousClicked() {
    this.updateCalendarDateByMonths(-1);
  }

  nextClicked() {
    this.updateCalendarDateByMonths(1);
  }

  get selectedMonthLabel(): string {
    return this.months[this.selectedMonthIndex] ?? '';
  }

  selectMonth(monthIndex: number) {
    if (monthIndex === this.selectedMonthIndex) {
      return;
    }
    this.setCalendarDate(monthIndex, this.selectedYear);
  }

  selectYear(year: number) {
    if (year === this.selectedYear) {
      return;
    }
    this.setCalendarDate(this.selectedMonthIndex, year);
  }

  private initializeYearOptions(): void {
    const currentDate = this.timezoneService.getCurrentDateOnly();
    const currentYear = currentDate.getFullYear();
    const startYear = Math.max(this.minDate.getFullYear(), currentYear - 5);
    const endYear = currentYear + 5;
    this.years = Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
    this.maxDate = new Date(endYear, 11, 31);
  }

  private updateCalendarDateByMonths(monthsToAdd: number): void {
    if (!this._dateAdapter) {
      return;
    }
    const updatedDate = this._dateAdapter.addCalendarMonths(this.calendar.activeDate, monthsToAdd);
    this.calendar.activeDate = this.clampDate(updatedDate);
    this.syncSelectionWithActiveDate();
  }

  private ensureActiveDateWithinBounds(): void {
    if (!(this.calendar.activeDate instanceof Date)) {
      return;
    }
    const clamped = this.clampDate(this.calendar.activeDate);
    if (clamped.getTime() !== this.calendar.activeDate.getTime()) {
      this.calendar.activeDate = clamped;
    }
  }

  private syncSelectionWithActiveDate(): void {
    if (!(this.calendar.activeDate instanceof Date)) {
      return;
    }
    this.selectedMonthIndex = this.calendar.activeDate.getMonth();
    this.selectedYear = this.calendar.activeDate.getFullYear();
  }

  private setCalendarDate(monthIndex: number, year: number): void {
    if (!this._dateAdapter) {
      return;
    }
    const currentDay =
      this.calendar.activeDate instanceof Date ? this.calendar.activeDate.getDate() : this.minDate.getDate();
    const newDate = this._dateAdapter.createDate(year, monthIndex, currentDay);
    this.calendar.activeDate = this.clampDate(newDate);
    this.syncSelectionWithActiveDate();
  }

  private clampDate(date: Date): Date {
    if (date < this.minDate) {
      return new Date(this.minDate);
    }
    if (this.maxDate && date > this.maxDate) {
      return new Date(this.maxDate);
    }
    return date;
  }
}
